/**
 * features/gallery/api.ts — queries para la Galería pública.
 *
 * Hooks tipados que envuelven `useQuery` (TanStack Query) sobre el singleton
 * `supabase`. Cada `queryKey` es estable por filtros y orden para que `F6`
 * (admin) pueda invalidar bloques específicos al editar contenido.
 *
 * Reglas:
 *  - Solo columnas que la UI consume (ahorra bytes y respeta RLS).
 *  - Filtros `is_published = true` + `deleted_at is null` en públicas.
 *  - Orden:
 *      - 'recent'      → `created_at desc` (novedades, default).
 *      - 'oldest'      → `created_at asc`  (más antiguas primero).
 *      - 'price_asc'   → precio del producto vinculado ascendente
 *                        (items sin producto quedan al final; se resuelve
 *                        en cliente porque `products.price_cop` es embed).
 *      - 'price_desc'  → precio descendente (mismo comportamiento).
 *  - Filtro por categoría se hace por `category_id` (resuelto desde el slug),
 *    no por join, para mantener el SELECT simple y respetar las políticas RLS.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase/client';
import type { Database } from '../../lib/supabase/types';

// ---------------------------------------------------------------------------
// Tipos derivados del schema. Solo las columnas que la UI lee.
// ---------------------------------------------------------------------------

type GalleryItemRow = Database['public']['Tables']['gallery_items']['Row'];
type GalleryCategoryRow = Database['public']['Tables']['gallery_categories']['Row'];

/** Item de galería tal como lo consumen las pages (subset de columnas). */
export interface GalleryItem {
  id: GalleryItemRow['id'];
  title: GalleryItemRow['title'];
  slug: GalleryItemRow['slug'];
  type: GalleryItemRow['type'];
  media_urls: GalleryItemRow['media_urls'];
  cover_url: GalleryItemRow['cover_url'];
  /** URL pública opcional de un video (YouTube/Vimeo) — la consume el
   *  `GalleryLightbox` para embeber el reproductor del detalle. */
  video_url: GalleryItemRow['video_url'];
  description_md: GalleryItemRow['description_md'];
  symbol_notes: GalleryItemRow['symbol_notes'];
  related_product_id: GalleryItemRow['related_product_id'];
  related_event_id: GalleryItemRow['related_event_id'];
  category_id: GalleryItemRow['category_id'];
  /**
   * Stock del producto vinculado vía `related_product_id` (embed PostgREST).
   *  · `null`  — no hay producto vinculado (pieza editorial sin venta) o
   *              pieza única sin contabilidad de stock.
   *  · `0`     — vendido.
   *  · `>0`    — disponible.
   * La UI lo usa para el filtro "Disponibilidad" en `/galeria`.
   */
  product_stock: number | null;
  /**
   * Precio del producto vinculado (`products.price_cop`) vía el mismo embed
   * PostgREST que `product_stock`. `null` cuando el item no tiene
   * `related_product_id`. La UI lo usa para ordenar "Por valor" en
   * `/galeria` — obras sin precio se colocan al final del listado.
   */
  product_price: number | null;
}

/** Categoría de galería (Cuadros, Bolsos, Cajas, Rituales, Procesos). */
export type GalleryCategory = GalleryCategoryRow;

/**
 * Modo de ordenamiento expuesto a la UI:
 *  · `'recent'`     — Novedades (created_at desc). Default.
 *  · `'oldest'`     — Más antiguas primero (created_at asc).
 *  · `'price_asc'`  — Por valor, menor a mayor (obras sin producto al final).
 *  · `'price_desc'` — Por valor, mayor a menor (obras sin producto al final).
 *
 * NOTA: se eliminó el modo `'sort'` (Curaduría manual del admin) — la clienta
 * confirmó que el concepto de curaduría no aplica a su contexto.
 */
export type GallerySortBy = 'recent' | 'oldest' | 'price_asc' | 'price_desc';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Columnas seleccionadas para `GalleryItem`. Compartido por lista + detalle.
// El embed `products(stock)` resuelve el FK `related_product_id` → `products.id`
// declarado en migration `0008_gallery.sql`, y trae el campo `stock` del
// producto vinculado para el filtro de disponibilidad en `/galeria`. Si el
// item no tiene `related_product_id`, PostgREST devuelve `products: null` y
// el `mapGalleryItem` lo materializa como `product_stock: null`.
const GALLERY_ITEM_SELECT =
  'id, title, slug, type, media_urls, cover_url, video_url, description_md, symbol_notes, related_product_id, related_event_id, category_id, products(stock, price_cop)';

/** Mapea fila cruda de Supabase al shape `GalleryItem` saneado. */
function mapGalleryItem(row: Record<string, unknown>): GalleryItem {
  // El embed PostgREST devuelve `products` como objeto o `null` cuando la FK
  // `related_product_id` es null. Extraemos `stock` y `price_cop` con guard
  // para tolerar cualquier forma (array, objeto, ausente).
  type ProductEmbed = { stock?: number | null; price_cop?: number | null };
  const productsEmbed = row.products as ProductEmbed | ProductEmbed[] | null | undefined;
  const product = Array.isArray(productsEmbed)
    ? (productsEmbed[0] ?? null)
    : (productsEmbed ?? null);
  const product_stock = product && typeof product.stock === 'number' ? product.stock : null;
  const product_price = product && typeof product.price_cop === 'number' ? product.price_cop : null;

  return {
    id: row.id as string,
    title: row.title as string,
    slug: row.slug as string,
    type: row.type as GalleryItemRow['type'],
    media_urls: (row.media_urls as string[] | null) ?? [],
    cover_url: (row.cover_url as string | null) ?? null,
    video_url: (row.video_url as string | null) ?? null,
    description_md: (row.description_md as string | null) ?? null,
    symbol_notes: (row.symbol_notes as GalleryItemRow['symbol_notes']) ?? {},
    related_product_id: (row.related_product_id as string | null) ?? null,
    related_event_id: (row.related_event_id as string | null) ?? null,
    category_id: (row.category_id as string | null) ?? null,
    product_stock,
    product_price,
  };
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Lista items publicados de la galería.
 *
 * - Si `categorySlug` está presente, primero resolvemos el `category_id` de la
 *   categoría correspondiente y filtramos `gallery_items` por ese FK. Lo
 *   hacemos en dos pasos (no en un join encadenado) para mantener el SELECT
 *   simple, alinear con el patrón de `useTherapies` y respetar RLS por tabla.
 * - Si la categoría no existe, devolvemos lista vacía (no error).
 * - Orden controlado por `sortBy`:
 *     - 'recent' → `created_at desc`
 *     - 'sort'   → `sort asc`
 */
export function useGalleryItems(
  categorySlug?: string,
  sortBy: GallerySortBy = 'recent',
): UseQueryResult<GalleryItem[], Error> {
  return useQuery<GalleryItem[], Error>({
    queryKey: ['gallery', 'list', categorySlug ?? '__all__', sortBy],
    queryFn: async (): Promise<GalleryItem[]> => {
      // Resolvemos category_id si viene un slug. Cuando no hay slug, salteamos.
      let categoryId: string | null = null;
      if (categorySlug) {
        const { data: cat, error: catErr } = await supabase
          .from('gallery_categories')
          .select('id')
          .eq('slug', categorySlug)
          .is('deleted_at', null)
          .maybeSingle();
        if (catErr) throw new Error(catErr.message);
        // Si la categoría no existe (o fue borrada lógicamente), devolvemos [].
        if (!cat) return [];
        categoryId = cat.id as string;
      }

      let q = supabase
        .from('gallery_items')
        .select(GALLERY_ITEM_SELECT)
        .eq('is_published', true)
        .is('deleted_at', null);

      if (categoryId) {
        q = q.eq('category_id', categoryId);
      }

      // Orden — cronológico se resuelve en el servidor; los modos por precio
      // requieren un sort en cliente porque `price_cop` viene por embed y
      // PostgREST no permite `.order('products.price_cop')` sin ambigüedad
      // cuando el embed puede ser null. Traemos ordenado por created_at desc
      // (default estable) y reordenamos abajo si el modo es price_*.
      q =
        sortBy === 'oldest'
          ? q.order('created_at', { ascending: true })
          : q.order('created_at', { ascending: false });

      const { data, error } = await q;
      if (error) throw new Error(error.message);
      const items = (data ?? []).map((row) => mapGalleryItem(row as Record<string, unknown>));

      if (sortBy === 'price_asc' || sortBy === 'price_desc') {
        // Obras sin `product_price` van SIEMPRE al final (independiente de la
        // dirección) — no tienen precio "conocido" para comparar. Entre
        // ellas se preserva el orden cronológico ya aplicado por el servidor.
        // `sort` es estable en JS moderno (V8/JavaScriptCore/SpiderMonkey).
        const dir = sortBy === 'price_asc' ? 1 : -1;
        items.sort((a, b) => {
          const ap = a.product_price;
          const bp = b.product_price;
          if (ap === null && bp === null) return 0;
          if (ap === null) return 1;
          if (bp === null) return -1;
          return (ap - bp) * dir;
        });
      }

      return items;
    },
  });
}

/** Catálogo de categorías ordenado por `sort asc`. */
export function useGalleryCategories(): UseQueryResult<GalleryCategory[], Error> {
  return useQuery<GalleryCategory[], Error>({
    queryKey: ['gallery_categories'],
    queryFn: async (): Promise<GalleryCategory[]> => {
      const { data, error } = await supabase
        .from('gallery_categories')
        .select('*')
        .is('deleted_at', null)
        .order('sort', { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as GalleryCategory[];
    },
  });
}

/**
 * Trae un único item publicado por slug. Pensado para deep-link via
 * `/galeria#<slug>` → al cargar la página el hash abre directamente el
 * lightbox de ese item.
 *
 * `enabled` solo cuando `slug` existe (evita disparar con string vacío).
 * `data === null` => item no encontrado (componente debe ignorar el hash).
 */
export function useGalleryItemBySlug(
  slug: string | undefined,
): UseQueryResult<GalleryItem | null, Error> {
  return useQuery<GalleryItem | null, Error>({
    queryKey: ['gallery', 'detail', slug ?? ''],
    enabled: Boolean(slug),
    queryFn: async (): Promise<GalleryItem | null> => {
      const { data, error } = await supabase
        .from('gallery_items')
        .select(GALLERY_ITEM_SELECT)
        .eq('slug', slug as string)
        .eq('is_published', true)
        .is('deleted_at', null)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!data) return null;
      return mapGalleryItem(data as Record<string, unknown>);
    },
  });
}
